/**
 * TriggerEngine - Active Trigger System (V2 Enhanced)
 *
 * This engine actively sets up DOM event listeners for campaign triggers.
 * When triggers fire, it emits events through the EventBus.
 *
 * V2 Architecture:
 * - SDK handles ONLY 'behavioral' triggers (user actions on-site)
 * - 'scheduled', 'system_event', and 'manual' triggers are server-side
 * - Supports both legacy CampaignTriggers and V2 BehavioralTrigger formats
 *
 * Supported behavioral events (10 triggers):
 * - exit_intent: Detects when user is about to leave the page
 * - scroll_depth: Fires when user scrolls to a certain percentage
 * - time_on_page / time_on_site: Fires after user has been on page for X seconds
 * - idle: Fires when no user activity for X seconds
 * - view_product: Fires when product page is viewed
 * - add_to_cart: Fires when item is added to cart
 * - remove_from_cart: Fires when item is removed from cart
 * - cart_abandoned: Fires when cart has items and no activity for X seconds
 * - click: Fires when specific element is clicked
 *
 * Usage:
 * ```typescript
 * const eventBus = new EventBus();
 * const engine = new TriggerEngine(eventBus);
 *
 * // Register a campaign - triggers are auto-setup
 * engine.registerCampaign(campaign);
 *
 * // Listen for trigger events
 * eventBus.on('trigger:fired', (data) => {
 *   console.log('Trigger fired:', data.triggerType);
 * });
 *
 * // Cleanup when done
 * engine.destroy();
 * ```
 */

import type { EventBus } from '../core/EventBus';
import type {
  Campaign,
  CampaignTriggers,
  CampaignTrigger,
  BehavioralTrigger,
  BehavioralEvent,
} from '../types/campaign.types';
import type { TriggerContext } from '../types/trigger.types';
import type {
  TriggerEngineConfig,
  RegisteredTrigger,
  TimeTrackerState,
  ScrollTrackerState,
  ExitIntentTrackerState,
  CartTrackerState,
} from '../types/trigger-engine.types';
import type { PageContext } from '../adapters/MicroAdapter';

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<TriggerEngineConfig> = {
  debug: false,
  debounceMs: 1000,
  exitIntentThreshold: 10,
  defaultScrollThreshold: 50,
  defaultTimeDelay: 30,
  defaultCartDelay: 300,
  defaultIdleTimeout: 60,
};

/**
 * Map legacy V2TriggerType to BehavioralEvent
 * This allows backward compatibility with existing campaigns
 */
const LEGACY_TO_BEHAVIORAL_MAP: Record<string, BehavioralEvent> = {
  'exit_intent': 'exit_intent',
  'scroll_depth': 'scroll_depth',
  'time_on_site': 'time_on_site',
  'time_on_page': 'time_on_page',
  'cart_abandoned': 'cart_abandoned',
  'view_product': 'view_product',
  'custom': 'click', // Map custom to click as closest equivalent
};

/**
 * Normalize trigger config to get behavioral event and config
 * Supports both legacy CampaignTriggers and V2 BehavioralTrigger formats
 */
function normalizeTriggerConfig(
  triggerConfig: CampaignTriggers | BehavioralTrigger | CampaignTrigger | null | undefined
): { event: BehavioralEvent; config: { delay?: number; conditions?: Record<string, any> } } | null {
  if (!triggerConfig) return null;

  // V2 BehavioralTrigger format: { type: 'behavioral', event: '...', config: { ... } }
  if ('type' in triggerConfig && triggerConfig.type === 'behavioral') {
    const behavioral = triggerConfig as BehavioralTrigger;
    return {
      event: behavioral.event,
      config: {
        // `??`, not `||`: 0 is a legitimate delay meaning "as soon as the
        // page is ready", and `||` silently discards it in favour of
        // `defaultTimeDelay` — so an author asking for immediate gets 30
        // seconds, with nothing anywhere to say why.
        delay: behavioral.config?.seconds ?? behavioral.config?.inactivitySeconds,
        conditions: {
          threshold: behavioral.config?.threshold,
          selector: behavioral.config?.selector,
          stepNumber: behavioral.config?.stepNumber,
        },
      },
    };
  }

  // V2 non-behavioral triggers (scheduled, system_event, manual) - not handled by SDK
  if ('type' in triggerConfig && ['scheduled', 'system_event', 'manual'].includes(triggerConfig.type as string)) {
    return null;
  }

  // Legacy CampaignTriggers format: { type: 'exit_intent', delay?: ..., conditions?: { ... } }
  const legacy = triggerConfig as CampaignTriggers;
  if (legacy.type) {
    const behavioralEvent = LEGACY_TO_BEHAVIORAL_MAP[legacy.type];
    if (behavioralEvent) {
      return {
        event: behavioralEvent,
        config: {
          delay: legacy.delay,
          conditions: legacy.conditions,
        },
      };
    }
  }

  return null;
}

export class TriggerEngine {
  private eventBus: EventBus;
  private config: Required<TriggerEngineConfig>;
  private triggers: Map<string, RegisteredTrigger>;
  private debounceTimers: Map<string, ReturnType<typeof setTimeout>>;

  // Global trackers (shared across campaigns)
  private scrollState: ScrollTrackerState;
  private exitIntentState: ExitIntentTrackerState;
  private timeState: TimeTrackerState;
  private cartState: CartTrackerState;

  // Track if global listeners are set up
  private globalListenersActive: boolean;

  // Page context for page detection (injected by HamlaSDK)
  private pageContext: PageContext | null = null;

  constructor(eventBus: EventBus, config?: TriggerEngineConfig) {
    this.eventBus = eventBus;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.triggers = new Map();
    this.debounceTimers = new Map();

    // Initialize tracker states
    this.scrollState = { maxScrollDepth: 0 };
    this.exitIntentState = {
      triggered: false,
      isMobile: this.detectMobileDevice(),
      lastScrollY: 0,
      scrollUpCount: 0,
    };
    this.timeState = {
      startTime: Date.now(),
      accumulatedTime: 0,
      isVisible: true,
    };
    this.cartState = {
      hasItems: false,
      lastActivityTime: Date.now(),
    };

    this.globalListenersActive = false;

    this.log('TriggerEngine initialized');
  }

  /**
   * Set page context for page type detection
   * Called by HamlaSDK after MicroAdapter collects page context
   */
  public setPageContext(pageContext: PageContext): void {
    this.pageContext = pageContext;
    this.log('Page context set');
  }

  /**
   * Set initial cart state (replaces async platformAdapter.getCart() call)
   * Called by HamlaSDK after MicroAdapter gets cart data
   */
  public setInitialCartState(hasItems: boolean): void {
    this.cartState.hasItems = hasItems;
    this.cartState.lastActivityTime = Date.now();
    if (hasItems) {
      this.startCartMonitoring();
    }
    this.log(`Initial cart state set: hasItems=${hasItems}`);
  }

  /**
   * Detect if device is mobile/tablet (touch-based)
   *
   * Uses multiple signals for reliable detection:
   * 1. Touch capability (primary signal)
   * 2. Screen size (secondary signal)
   * 3. User agent (fallback)
   */
  private detectMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;

    // Primary: Check for touch capability
    const hasTouch = 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;

    // Secondary: Check screen size (mobile/tablet typically < 1024px)
    const isSmallScreen = window.innerWidth < 1024;

    // Fallback: User agent check for common mobile patterns
    const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    // Consider mobile if touch-enabled AND (small screen OR mobile UA)
    // This avoids false positives on touch-enabled laptops
    return hasTouch && (isSmallScreen || mobileUA);
  }

  /**
   * Register a campaign and set up its triggers
   *
   * V2 Enhanced: Supports both legacy CampaignTriggers and V2 CampaignTrigger formats.
   * Only behavioral triggers are handled by SDK - scheduled, system_event, and manual
   * triggers are processed server-side.
   */
  public registerCampaign(campaign: Campaign): void {
    // Get trigger config (may be null/undefined, legacy or V2 format)
    const rawTriggerConfig = campaign.triggers as CampaignTriggers | CampaignTrigger | null | undefined;

    // Normalize to behavioral event format
    const normalized = normalizeTriggerConfig(rawTriggerConfig);

    // If no behavioral trigger, skip (server-side triggers or no trigger defined)
    if (!normalized) {
      // Check if it's a non-behavioral V2 trigger
      if (rawTriggerConfig && 'type' in rawTriggerConfig) {
        const triggerType = (rawTriggerConfig as { type: string }).type;
        if (['scheduled', 'system_event', 'manual'].includes(triggerType)) {
          this.log(`Campaign "${campaign.name}" has ${triggerType} trigger (server-side), skipping SDK registration`);
          return;
        }
      }
      this.log(`Campaign "${campaign.name}" has no behavioral trigger config, skipping registration`);
      return;
    }

    const triggerId = `${campaign.id}-${normalized.event}`;

    // Check if already registered
    if (this.triggers.has(triggerId)) {
      this.log(`Trigger already registered: ${triggerId}`);
      return;
    }

    // Setup global listeners if not already active
    if (!this.globalListenersActive) {
      this.setupGlobalListeners();
    }

    // Create normalized config for internal use
    const normalizedConfig: CampaignTriggers = {
      type: normalized.event as any, // Map behavioral event to legacy type for internal use
      delay: normalized.config.delay,
      conditions: normalized.config.conditions,
    };

    // Create and store trigger registration
    const registration: RegisteredTrigger = {
      id: triggerId,
      campaignId: campaign.id,
      campaignName: campaign.name,
      type: normalized.event,
      config: normalizedConfig,
      cleanup: () => {}, // Will be set by specific trigger setup
      fired: false,
      registeredAt: Date.now(),
    };

    // Set up specific trigger type
    const cleanup = this.setupTrigger(registration);
    registration.cleanup = cleanup;

    this.triggers.set(triggerId, registration);

    // Emit registration event
    this.eventBus.emit('trigger:registered', {
      timestamp: Date.now(),
      triggerType: normalized.event,
      campaignId: campaign.id,
      campaignName: campaign.name,
    });

    this.log(`Registered trigger: ${normalized.event} for campaign "${campaign.name}"`);
  }

  /**
   * Register a trigger from a server instruction.
   * Lightweight alternative to registerCampaign() — no Campaign object needed.
   */
  public registerTrigger(
    campaignId: string,
    event: string,
    config?: Record<string, unknown>
  ): void {
    const triggerId = `${campaignId}-${event}`;

    if (this.triggers.has(triggerId)) {
      this.log(`Trigger already registered: ${triggerId}`);
      return;
    }

    if (!this.globalListenersActive) {
      this.setupGlobalListeners();
    }

    const normalizedConfig: CampaignTriggers = {
      type: event as any,
      // Same reason as above, and this is the one that matters most: the
      // picker writes `duration`, the runtime reads `delay`, and a 0 has to
      // survive that translation intact.
      delay:
        (config?.delay as number | undefined) ??
        (config?.duration as number | undefined) ??
        (config?.seconds as number | undefined),
      conditions: config?.conditions as any,
    };

    const registration: RegisteredTrigger = {
      id: triggerId,
      campaignId,
      campaignName: `instruction:${event}`,
      type: event as BehavioralEvent,
      config: normalizedConfig,
      cleanup: () => {},
      fired: false,
      registeredAt: Date.now(),
    };

    const cleanup = this.setupTrigger(registration);
    registration.cleanup = cleanup;
    this.triggers.set(triggerId, registration);

    this.log(`Registered trigger from instruction: ${event} for campaign ${campaignId.substring(0, 12)}...`);
  }

  /**
   * Unregister a campaign's triggers
   */
  public unregisterCampaign(campaignId: string): void {
    for (const [id, trigger] of this.triggers) {
      if (trigger.campaignId === campaignId) {
        trigger.cleanup();
        this.triggers.delete(id);
        this.log(`Unregistered trigger: ${id}`);
      }
    }
  }

  /**
   * Get all registered triggers
   */
  public getRegisteredTriggers(): RegisteredTrigger[] {
    return Array.from(this.triggers.values());
  }

  /**
   * Manually update cart state (for cart abandonment trigger)
   */
  public updateCartState(hasItems: boolean, cartValue?: number): void {
    const previousHasItems = this.cartState.hasItems;
    this.cartState.hasItems = hasItems;
    this.cartState.lastActivityTime = Date.now();

    // Emit cart updated event
    this.eventBus.emit('cart:updated', {
      timestamp: Date.now(),
      itemCount: hasItems ? 1 : 0, // Simplified, actual count would come from platform
      cartValue: cartValue ?? 0,
    });

    // If cart now has items and previously didn't, start monitoring
    if (hasItems && !previousHasItems) {
      this.startCartMonitoring();
    }

    this.log(`Cart state updated: hasItems=${hasItems}`);
  }

  /**
   * Clean up all listeners and triggers
   */
  public destroy(): void {
    // Cleanup all registered triggers
    for (const trigger of this.triggers.values()) {
      trigger.cleanup();
    }
    this.triggers.clear();

    // Cleanup global listeners
    this.cleanupGlobalListeners();

    // Clear debounce timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();

    this.log('TriggerEngine destroyed');
  }

  /**
   * Setup global listeners (scroll, exit intent, visibility)
   *
   * Exit intent detection is device-aware:
   * - Desktop: Mouse leaving viewport top (standard exit intent)
   * - Mobile: Tab/app switch, rapid scroll-up gesture, or back button
   */
  private setupGlobalListeners(): void {
    if (typeof window === 'undefined') {
      this.log('No window object, skipping global listeners (SSR)');
      return;
    }

    const isMobile = this.exitIntentState.isMobile;
    this.log(`Setting up global listeners (device: ${isMobile ? 'mobile' : 'desktop'})`);

    // Scroll listener (for scroll_depth triggers)
    this.scrollState.scrollHandler = () => {
      const scrollPercent = this.calculateScrollDepth();
      if (scrollPercent > this.scrollState.maxScrollDepth) {
        this.scrollState.maxScrollDepth = scrollPercent;
        this.checkScrollTriggers(scrollPercent);
      }
    };
    window.addEventListener('scroll', this.scrollState.scrollHandler, { passive: true });

    // ==========================================
    // Exit Intent Detection (Device-Aware)
    // ==========================================
    if (isMobile) {
      // MOBILE EXIT INTENT DETECTION
      // Uses multiple signals that indicate user is about to leave

      // 1. Visibility change (tab/app switch) - strongest mobile signal
      // When user switches to another app or tab, they're likely leaving
      this.exitIntentState.visibilityHandler = () => {
        if (document.hidden && !this.exitIntentState.triggered) {
          // Only trigger if user has been on page for at least 3 seconds
          // This prevents false positives from quick tab switches
          const timeOnPage = this.getTimeOnSite();
          if (timeOnPage >= 3000) {
            this.log('Mobile exit intent: visibility change (tab/app switch)');
            this.checkExitIntentTriggers(-1); // -1 indicates mobile trigger
          }
        }
      };
      document.addEventListener('visibilitychange', this.exitIntentState.visibilityHandler);

      // 2. Rapid scroll-up detection (swipe up to leave gesture)
      // When user rapidly scrolls back to top, they're often about to leave
      this.exitIntentState.lastScrollY = window.scrollY;
      this.exitIntentState.scrollUpCount = 0;

      this.exitIntentState.mobileScrollHandler = () => {
        const currentY = window.scrollY;
        const lastY = this.exitIntentState.lastScrollY || 0;
        const scrollDelta = lastY - currentY;

        // Detect rapid scroll up (> 50px per frame)
        if (scrollDelta > 50 && currentY < 200) {
          this.exitIntentState.scrollUpCount = (this.exitIntentState.scrollUpCount || 0) + 1;

          // Trigger after 3 consecutive rapid scroll-ups near top of page
          if (this.exitIntentState.scrollUpCount >= 3 && !this.exitIntentState.triggered) {
            this.log('Mobile exit intent: rapid scroll up near top');
            this.checkExitIntentTriggers(-1);
          }
        } else if (scrollDelta < 0) {
          // Reset count if scrolling down
          this.exitIntentState.scrollUpCount = 0;
        }

        this.exitIntentState.lastScrollY = currentY;
      };
      window.addEventListener('scroll', this.exitIntentState.mobileScrollHandler, { passive: true });

      // 3. Back button detection (popstate)
      // Note: This fires AFTER navigation, so we use it as a secondary signal
      this.exitIntentState.popstateHandler = () => {
        if (!this.exitIntentState.triggered) {
          this.log('Mobile exit intent: back button pressed');
          this.checkExitIntentTriggers(-1);
        }
      };
      window.addEventListener('popstate', this.exitIntentState.popstateHandler);

    } else {
      // DESKTOP EXIT INTENT DETECTION
      // Classic mouse-based detection when cursor leaves viewport top
      this.exitIntentState.mouseleaveHandler = (e: MouseEvent) => {
        if (e.clientY < this.config.exitIntentThreshold) {
          this.checkExitIntentTriggers(e.clientY);
        }
      };
      document.addEventListener('mouseleave', this.exitIntentState.mouseleaveHandler);
    }

    // Visibility change listener (for accurate time tracking - separate from exit intent)
    this.timeState.visibilityHandler = () => {
      if (document.hidden) {
        // Page hidden - accumulate time and pause
        this.timeState.accumulatedTime += Date.now() - this.timeState.startTime;
        this.timeState.isVisible = false;
      } else {
        // Page visible - restart timer
        this.timeState.startTime = Date.now();
        this.timeState.isVisible = true;
      }
    };
    document.addEventListener('visibilitychange', this.timeState.visibilityHandler);

    this.globalListenersActive = true;
    this.log('Global listeners set up');
  }

  /**
   * Cleanup global listeners
   */
  private cleanupGlobalListeners(): void {
    if (typeof window === 'undefined') return;

    // Scroll listener
    if (this.scrollState.scrollHandler) {
      window.removeEventListener('scroll', this.scrollState.scrollHandler);
    }

    // Exit intent listeners (desktop)
    if (this.exitIntentState.mouseleaveHandler) {
      document.removeEventListener('mouseleave', this.exitIntentState.mouseleaveHandler);
    }

    // Exit intent listeners (mobile)
    if (this.exitIntentState.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.exitIntentState.visibilityHandler);
    }
    if (this.exitIntentState.mobileScrollHandler) {
      window.removeEventListener('scroll', this.exitIntentState.mobileScrollHandler);
    }
    if (this.exitIntentState.popstateHandler) {
      window.removeEventListener('popstate', this.exitIntentState.popstateHandler);
    }

    // Time tracking
    if (this.timeState.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.timeState.visibilityHandler);
    }

    if (this.timeState.timerId) {
      clearTimeout(this.timeState.timerId);
    }

    if (this.cartState.timerId) {
      clearInterval(this.cartState.timerId);
    }

    this.globalListenersActive = false;
    this.log('Global listeners cleaned up');
  }

  /**
   * Setup specific trigger type
   *
   * V2 Enhanced: Supports all BehavioralEvent types
   */
  private setupTrigger(trigger: RegisteredTrigger): () => void {
    switch (trigger.type) {
      // Exit & Engagement Events
      case 'exit_intent':
        return this.setupExitIntent(trigger);
      case 'scroll_depth':
        return this.setupScrollDepth(trigger);
      case 'time_on_site':
      case 'time_on_page': // Alias
        return this.setupTimeOnSite(trigger);
      case 'idle':
        return this.setupIdle(trigger);

      // E-commerce Events - Page Type
      case 'view_product':
        return this.setupViewProduct(trigger);

      // E-commerce Events
      case 'cart_abandoned':
        return this.setupCartAbandoned(trigger);
      case 'add_to_cart':
        return this.setupAddToCart(trigger);
      case 'remove_from_cart':
        return this.setupRemoveFromCart(trigger);

      // Click Events
      case 'click':
        return this.setupClick(trigger);

      default:
        this.log(`Unknown trigger type: ${trigger.type}`);
        return () => {};
    }
  }

  /**
   * Setup exit intent trigger
   */
  private setupExitIntent(_trigger: RegisteredTrigger): () => void {
    // Exit intent uses global listener, no specific setup needed
    // Just return empty cleanup
    return () => {};
  }

  /**
   * Setup scroll depth trigger
   */
  private setupScrollDepth(trigger: RegisteredTrigger): () => void {
    // Scroll uses global listener, check immediately if already scrolled
    const threshold = trigger.config.conditions?.threshold ?? this.config.defaultScrollThreshold;

    if (this.scrollState.maxScrollDepth >= threshold) {
      // Already scrolled past threshold, fire immediately
      this.fireTrigger(trigger);
    }

    return () => {};
  }

  /**
   * Setup time on site trigger
   */
  private setupTimeOnSite(trigger: RegisteredTrigger): () => void {
    const delaySeconds = trigger.config.delay ?? this.config.defaultTimeDelay;
    const delayMs = delaySeconds * 1000;

    // Calculate remaining time considering already accumulated time
    const alreadySpent = this.getTimeOnSite();
    const remainingMs = Math.max(0, delayMs - alreadySpent);

    const timerId = setTimeout(() => {
      this.fireTrigger(trigger);
    }, remainingMs);

    return () => clearTimeout(timerId);
  }

  /**
   * Setup view_product trigger
   *
   * Fires when user is viewing a product page.
   * Supports two matching modes:
   * 1. pageType (semantic): Match by page type (product)
   * 2. page (URL pattern): Match by URL path pattern (fallback)
   */
  private setupViewProduct(trigger: RegisteredTrigger): () => void {
    const conditions = trigger.config.conditions;
    const targetPageType = conditions?.pageType;  // Semantic type (preferred)
    const targetPage = conditions?.page;          // URL pattern (fallback)

    // Use page context injected by HamlaSDK (from MicroAdapter)
    const pageContext = this.pageContext;
    const currentPage = typeof window !== 'undefined' ? window.location.pathname : '';

    let matches = false;

    // 1. Semantic page type matching (preferred - uses page context)
    if (targetPageType && pageContext) {
      matches = pageContext.pageType === targetPageType;
      this.log(`Page type match: ${pageContext.pageType} === ${targetPageType} = ${matches}`);
    }
    // 2. URL pattern matching (fallback)
    else if (targetPage) {
      matches = this.pageMatches(currentPage, targetPage);
      this.log(`URL pattern match: ${currentPage} matches ${targetPage} = ${matches}`);
    }
    // 3. No target = any page matches
    else {
      matches = true;
      this.log('No page target specified, matching any page');
    }

    if (matches) {
      // Small delay for page to be ready
      const timerId = setTimeout(() => {
        // Include pageContext in trigger context
        this.fireTrigger(trigger, { pageInfo: pageContext ?? undefined });
      }, 100);

      return () => clearTimeout(timerId);
    }

    return () => {};
  }

  /**
   * Setup cart abandoned trigger
   *
   * Initial cart state is set via setInitialCartState() called by HamlaSDK
   * after MicroAdapter fetches cart data. Cart monitoring starts automatically
   * when items are present.
   */
  private setupCartAbandoned(_trigger: RegisteredTrigger): () => void {
    // Cart abandonment is handled by cart monitoring.
    // Initial cart state is injected via setInitialCartState() before triggers are armed.
    return () => {};
  }

  /**
   * Setup idle trigger (V2)
   * Fires when user has no activity for X seconds
   */
  private setupIdle(trigger: RegisteredTrigger): () => void {
    const idleSeconds = trigger.config.delay ?? this.config.defaultIdleTimeout;
    const idleMs = idleSeconds * 1000;

    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let lastActivity = Date.now();

    const resetIdleTimer = () => {
      lastActivity = Date.now();
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
      idleTimer = setTimeout(() => {
        this.fireTrigger(trigger, {
          timeOnSite: (Date.now() - lastActivity) / 1000,
        });
      }, idleMs);
    };

    // Activity events to listen for
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const activityHandler = () => resetIdleTimer();

    // Set up listeners
    if (typeof window !== 'undefined') {
      activityEvents.forEach(event => {
        window.addEventListener(event, activityHandler, { passive: true });
      });
      // Start the idle timer
      resetIdleTimer();
    }

    // Return cleanup function
    return () => {
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
      if (typeof window !== 'undefined') {
        activityEvents.forEach(event => {
          window.removeEventListener(event, activityHandler);
        });
      }
    };
  }

  /**
   * Setup add to cart trigger (V2)
   * Listens for cart:updated events from EventBus
   */
  private setupAddToCart(trigger: RegisteredTrigger): () => void {
    // Listen for cart updates through EventBus
    const handler = (data: { itemCount?: number; cartValue?: number }) => {
      if (data.itemCount && data.itemCount > 0) {
        this.fireTrigger(trigger, {
          justAddedToCart: true,  // Signal that user just added to cart
          hasCartItems: true,
        });
      }
    };

    // Subscribe to cart:updated events
    this.eventBus.on('cart:updated', handler);

    // Return cleanup function
    return () => {
      this.eventBus.off('cart:updated', handler);
    };
  }

  /**
   * Setup remove from cart trigger (V2)
   * Listens for cart:item_removed events from EventBus
   */
  private setupRemoveFromCart(trigger: RegisteredTrigger): () => void {
    // Listen for cart item removed events through EventBus
    const handler = () => {
      this.fireTrigger(trigger, {
        justRemovedFromCart: true,
      });
    };

    // Subscribe to cart:item_removed events
    this.eventBus.on('cart:item_removed', handler);

    // Return cleanup function
    return () => {
      this.eventBus.off('cart:item_removed', handler);
    };
  }

  /**
   * Setup click trigger (V2)
   * Fires when specific element is clicked (by CSS selector)
   */
  private setupClick(trigger: RegisteredTrigger): () => void {
    const selector = trigger.config.conditions?.selector as string | undefined;

    if (!selector) {
      this.log('Click trigger requires a selector in conditions');
      return () => {};
    }

    if (typeof document === 'undefined') {
      return () => {};
    }

    const handler = (e: Event) => {
      const target = e.target as Element;
      if (target?.matches?.(selector) || target?.closest?.(selector)) {
        this.fireTrigger(trigger);
      }
    };

    document.addEventListener('click', handler);

    return () => {
      document.removeEventListener('click', handler);
    };
  }

  /**
   * Start monitoring cart for abandonment
   */
  private startCartMonitoring(): void {
    // Clear existing timer
    if (this.cartState.timerId) {
      clearInterval(this.cartState.timerId);
    }

    // Check every 10 seconds
    this.cartState.timerId = setInterval(() => {
      this.checkCartAbandonmentTriggers();
    }, 10000);
  }

  /**
   * Check all exit intent triggers
   *
   * IMPORTANT: Only fires ONE trigger event for all campaigns with exit_intent.
   * The orchestration engine will decide which campaign to show based on scoring.
   * This prevents duplicate notifications when multiple campaigns have the same trigger.
   */
  private checkExitIntentTriggers(mouseY: number): void {
    // Find all unfired exit_intent triggers
    const exitIntentTriggers = Array.from(this.triggers.values())
      .filter(t => t.type === 'exit_intent' && !t.fired);

    if (exitIntentTriggers.length === 0) return;

    // Mark ALL exit_intent triggers as fired to prevent duplicates
    for (const trigger of exitIntentTriggers) {
      trigger.fired = true;
    }

    // Emit a SINGLE trigger:fired event (use first trigger as representative)
    // The SDK will run orchestration across ALL campaigns with exit_intent
    const firstTrigger = exitIntentTriggers[0];
    const context: TriggerContext = {
      isExitIntent: true,
      mouseY,
      timeOnSite: this.getTimeOnSite() / 1000,
      scrollDepth: this.scrollState.maxScrollDepth,
      currentPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    this.eventBus.emit('trigger:fired', {
      timestamp: Date.now(),
      triggerType: 'exit_intent',
      // Pass first campaign as context, but SDK will evaluate ALL campaigns
      campaignId: firstTrigger.campaignId,
      campaignName: firstTrigger.campaignName,
      context,
      // Signal that this is a consolidated trigger for multiple campaigns
      _allCampaignIds: exitIntentTriggers.map(t => t.campaignId),
    });

    this.log(`Exit intent triggered for ${exitIntentTriggers.length} campaigns (consolidated)`);
  }

  /**
   * Check all scroll depth triggers
   *
   * IMPORTANT: Only fires ONE trigger event for all campaigns that meet their scroll threshold.
   * The orchestration engine will decide which campaign to show based on scoring.
   */
  private checkScrollTriggers(currentDepth: number): void {
    // Find all unfired scroll_depth triggers that meet their threshold
    const matchingTriggers = Array.from(this.triggers.values())
      .filter(t => {
        if (t.type !== 'scroll_depth' || t.fired) return false;
        const threshold = t.config.conditions?.threshold ?? this.config.defaultScrollThreshold;
        return currentDepth >= threshold;
      });

    if (matchingTriggers.length === 0) return;

    // Mark ALL matching triggers as fired to prevent duplicates
    for (const trigger of matchingTriggers) {
      trigger.fired = true;
    }

    // Emit a SINGLE trigger:fired event
    const firstTrigger = matchingTriggers[0];
    const context: TriggerContext = {
      scrollDepth: currentDepth,
      timeOnSite: this.getTimeOnSite() / 1000,
      currentPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    this.eventBus.emit('trigger:fired', {
      timestamp: Date.now(),
      triggerType: 'scroll_depth',
      campaignId: firstTrigger.campaignId,
      campaignName: firstTrigger.campaignName,
      context,
      _allCampaignIds: matchingTriggers.map(t => t.campaignId),
    });

    this.log(`Scroll depth triggered for ${matchingTriggers.length} campaigns (consolidated)`);
  }

  /**
   * Check all cart abandonment triggers
   *
   * IMPORTANT: Only fires ONE trigger event for all campaigns that meet their delay threshold.
   * The orchestration engine will decide which campaign to show based on scoring.
   */
  private checkCartAbandonmentTriggers(): void {
    if (!this.cartState.hasItems) return;

    const secondsSinceActivity = (Date.now() - this.cartState.lastActivityTime) / 1000;

    // Find all unfired cart_abandoned triggers that meet their delay threshold
    const matchingTriggers = Array.from(this.triggers.values())
      .filter(t => {
        if (t.type !== 'cart_abandoned' || t.fired) return false;
        const delay = t.config.delay ?? this.config.defaultCartDelay;
        return secondsSinceActivity >= delay;
      });

    if (matchingTriggers.length === 0) return;

    // Mark ALL matching triggers as fired to prevent duplicates
    for (const trigger of matchingTriggers) {
      trigger.fired = true;
    }

    // Emit a SINGLE trigger:fired event
    const firstTrigger = matchingTriggers[0];
    const context: TriggerContext = {
      hasCartItems: true,
      secondsSinceCartActivity: secondsSinceActivity,
      timeOnSite: this.getTimeOnSite() / 1000,
      scrollDepth: this.scrollState.maxScrollDepth,
      currentPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    this.eventBus.emit('trigger:fired', {
      timestamp: Date.now(),
      triggerType: 'cart_abandoned',
      campaignId: firstTrigger.campaignId,
      campaignName: firstTrigger.campaignName,
      context,
      _allCampaignIds: matchingTriggers.map(t => t.campaignId),
    });

    this.log(`Cart abandonment triggered for ${matchingTriggers.length} campaigns (consolidated)`);
  }

  /**
   * Fire a trigger (with debouncing)
   */
  private fireTrigger(trigger: RegisteredTrigger, context?: TriggerContext): void {
    // Skip if already fired
    if (trigger.fired) return;

    // Check debounce
    const debounceKey = `${trigger.campaignId}-${trigger.type}`;
    if (this.debounceTimers.has(debounceKey)) {
      return;
    }

    // Mark as fired
    trigger.fired = true;

    // Set debounce timer
    const timer = setTimeout(() => {
      this.debounceTimers.delete(debounceKey);
    }, this.config.debounceMs);
    this.debounceTimers.set(debounceKey, timer);

    // Build full context
    const fullContext: TriggerContext = {
      ...context,
      timeOnSite: this.getTimeOnSite() / 1000,
      scrollDepth: this.scrollState.maxScrollDepth,
      currentPage: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    // Emit trigger:fired event
    this.eventBus.emit('trigger:fired', {
      timestamp: Date.now(),
      triggerType: trigger.type,
      campaignId: trigger.campaignId,
      campaignName: trigger.campaignName,
      context: fullContext,
    });

    this.log(`Trigger fired: ${trigger.type} for campaign "${trigger.campaignName}"`);
  }

  /**
   * Calculate current scroll depth percentage
   */
  private calculateScrollDepth(): number {
    if (typeof window === 'undefined') return 0;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    if (scrollHeight <= clientHeight) return 100;

    const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
    return Math.min(100, Math.max(0, scrollPercent));
  }

  /**
   * Get total time on site (accounting for visibility)
   */
  private getTimeOnSite(): number {
    if (this.timeState.isVisible) {
      return this.timeState.accumulatedTime + (Date.now() - this.timeState.startTime);
    }
    return this.timeState.accumulatedTime;
  }

  /**
   * Check if current page matches target page pattern
   */
  private pageMatches(current: string, target?: string): boolean {
    if (!target) return true; // No target = any page

    // Exact match
    if (current === target) return true;

    // Contains match
    if (current.includes(target)) return true;

    // Wildcard match (e.g., /products/*)
    if (target.endsWith('/*')) {
      const prefix = target.slice(0, -2);
      if (current.startsWith(prefix)) return true;
    }

    return false;
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: Record<string, unknown>): void {
    if (this.config.debug) {
      if (data) {
        console.log(`[TriggerEngine] ${message}`, data);
      } else {
        console.log(`[TriggerEngine] ${message}`);
      }
    }
  }
}
